import { useEffect, useMemo, useRef, useState } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, FileSpreadsheet, FileUp, Percent } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

type RegraImportada = {
  productCod: string;
  vendaMinima: number | null;
  comissaoVendaMinima: number | null;
  venda2: number | null;
  comissaoVenda2: number | null;
  venda3: number | null;
  comissaoVenda3: number | null;
};

const COLUNAS_TEMPLATE = {
  codigo: "CÓDIGO",
  descricao: "DESCRIÇÃO",
  preco: "PREÇO DE VENDA CADASTRADO",
  vendaMinima: "VENDA MÍNIMA",
  comissaoVendaMinima: "COMISSÃO VENDA MÍNIMA",
  venda2: "VENDA 2",
  comissaoVenda2: "COMISSÃO VENDA 2",
  venda3: "VENDA 3",
  comissaoVenda3: "COMISSÃO VENDA 3",
};

function numeroOpcional(valor: unknown): number | null {
  if (valor === null || valor === undefined || String(valor).trim() === "") return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  const texto = String(valor).trim();
  const numero = texto.includes(",")
    ? Number(texto.replace(/\./g, "").replace(",", "."))
    : Number(texto);
  return Number.isFinite(numero) ? numero : null;
}

function formatarData(valor: unknown) {
  if (!valor) return "-";
  const texto = String(valor);
  const data = texto.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(data)) {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  }
  return new Date(texto).toLocaleDateString("pt-BR");
}

function formatarDataHora(valor: unknown) {
  if (!valor) return "-";
  return new Date(String(valor)).toLocaleString("pt-BR");
}

function formatarMoeda(valor: unknown) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(valor || 0));
}

function formatarPercentual(valor: unknown) {
  return `${Number(valor || 0).toLocaleString("pt-BR", { maximumFractionDigits: 4 })}%`;
}

function nomesVendedores(valor: unknown) {
  try {
    const lista = JSON.parse(String(valor || "[]"));
    if (!Array.isArray(lista)) return "-";
    return lista.map((vendedor) => vendedor.nome || vendedor.vendedorNome || vendedor.id).join(", ") || "-";
  } catch {
    return "-";
  }
}

function SeletorVendedores({
  vendedores,
  selecionados,
  onChange,
  disabled,
}: {
  vendedores: any[];
  selecionados: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}) {
  const todosSelecionados = vendedores.length > 0 && vendedores.every((vendedor) => selecionados.includes(Number(vendedor.id)));
  const alternarTodos = () => onChange(todosSelecionados ? [] : vendedores.map((vendedor) => Number(vendedor.id)));
  const alternar = (id: number) => onChange(selecionados.includes(id) ? selecionados.filter((atual) => atual !== id) : [...selecionados, id]);

  return (
    <div className="rounded-md border p-3">
      <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={todosSelecionados} onChange={alternarTodos} disabled={disabled || vendedores.length === 0} />
        Selecionar todos
      </label>
      <div className="grid max-h-44 grid-cols-1 gap-2 overflow-y-auto md:grid-cols-2">
        {vendedores.length === 0 ? (
          <span className="text-sm text-muted-foreground">Nenhum vendedor com Visualizar somente vinculado para este emitente.</span>
        ) : vendedores.map((vendedor) => (
          <label key={vendedor.id} className="flex cursor-pointer items-center gap-2 text-sm">
            <input type="checkbox" checked={selecionados.includes(Number(vendedor.id))} onChange={() => alternar(Number(vendedor.id))} disabled={disabled} />
            <span>{vendedor.nome || vendedor.email}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function ComissaoAcaoVenda() {
  const { user } = useGoogleAuth();
  const utils = trpc.useUtils();
  const [aba, setAba] = useState("configurar");
  const [emitenteConfiguracaoId, setEmitenteConfiguracaoId] = useState<string>("");
  const [dataInicioConfiguracao, setDataInicioConfiguracao] = useState("");
  const [dataFimConfiguracao, setDataFimConfiguracao] = useState("");
  const [vendedoresConfiguracao, setVendedoresConfiguracao] = useState<number[]>([]);
  const [arquivoConfiguracao, setArquivoConfiguracao] = useState<File | null>(null);
  const [regrasImportadas, setRegrasImportadas] = useState<RegraImportada[]>([]);
  const [emitenteRelatorioId, setEmitenteRelatorioId] = useState<string>("");
  const [dataInicioRelatorio, setDataInicioRelatorio] = useState("");
  const [dataFimRelatorio, setDataFimRelatorio] = useState("");
  const [vendedoresRelatorio, setVendedoresRelatorio] = useState<number[]>([]);
  const inputArquivo = useRef<HTMLInputElement>(null);

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const emitenteConfiguracaoNumero = Number(emitenteConfiguracaoId || 0);
  const emitenteRelatorioNumero = Number(emitenteRelatorioId || 0);
  const { data: vendedoresConfiguracaoData = [] } = trpc.comissaoAcaoVenda.vendedores.useQuery(
    { emitenteId: emitenteConfiguracaoNumero },
    { enabled: emitenteConfiguracaoNumero > 0 }
  );
  const { data: produtosConfiguracao = [] } = trpc.comissaoAcaoVenda.produtos.useQuery(
    { emitenteId: emitenteConfiguracaoNumero },
    { enabled: emitenteConfiguracaoNumero > 0 }
  );
  const { data: vendedoresRelatorioData = [] } = trpc.comissaoAcaoVenda.vendedores.useQuery(
    { emitenteId: emitenteRelatorioNumero },
    { enabled: emitenteRelatorioNumero > 0 }
  );
  const { data: configuracoes = [] } = trpc.comissaoAcaoVenda.configuracoes.useQuery();
  const { data: relatorios = [] } = trpc.comissaoAcaoVenda.relatorios.useQuery();

  useEffect(() => {
    const emitentePadrao = (user as any)?.emitenteId;
    if (emitentePadrao && !emitenteConfiguracaoId) setEmitenteConfiguracaoId(String(emitentePadrao));
    if (emitentePadrao && !emitenteRelatorioId) setEmitenteRelatorioId(String(emitentePadrao));
  }, [user, emitenteConfiguracaoId, emitenteRelatorioId]);

  useEffect(() => {
    setVendedoresConfiguracao([]);
    setArquivoConfiguracao(null);
    setRegrasImportadas([]);
  }, [emitenteConfiguracaoId]);

  useEffect(() => {
    setVendedoresRelatorio([]);
  }, [emitenteRelatorioId]);

  const criarConfiguracao = trpc.comissaoAcaoVenda.criarConfiguracao.useMutation({
    onSuccess: async () => {
      await utils.comissaoAcaoVenda.configuracoes.invalidate();
      setArquivoConfiguracao(null);
      setRegrasImportadas([]);
      if (inputArquivo.current) inputArquivo.current.value = "";
      toast.success("Configuração de comissão salva.");
    },
    onError: (erro) => toast.error(erro.message),
  });

  const emitirRelatorio = trpc.comissaoAcaoVenda.emitirRelatorio.useMutation({
    onSuccess: async (resultado) => {
      await utils.comissaoAcaoVenda.relatorios.invalidate();
      toast.success(`Relatório emitido com ${resultado.totalItens} item(ns).`);
    },
    onError: (erro) => toast.error(erro.message),
  });

  const produtosPorCodigo = useMemo(() => new Set((produtosConfiguracao as any[]).map((produto) => String(produto.productCod).trim().toUpperCase())), [produtosConfiguracao]);

  const baixarTemplate = () => {
    if (!emitenteConfiguracaoNumero) return toast.error("Selecione um emitente antes de baixar o template.");
    const linhas = (produtosConfiguracao as any[]).map((produto) => ({
      [COLUNAS_TEMPLATE.codigo]: produto.productCod,
      [COLUNAS_TEMPLATE.descricao]: produto.productDescription || "",
      [COLUNAS_TEMPLATE.preco]: Number(produto.precoVendaCadastrado || 0),
      [COLUNAS_TEMPLATE.vendaMinima]: "",
      [COLUNAS_TEMPLATE.comissaoVendaMinima]: "",
      [COLUNAS_TEMPLATE.venda2]: "",
      [COLUNAS_TEMPLATE.comissaoVenda2]: "",
      [COLUNAS_TEMPLATE.venda3]: "",
      [COLUNAS_TEMPLATE.comissaoVenda3]: "",
    }));
    const planilha = XLSX.utils.json_to_sheet(linhas);
    const livro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(livro, planilha, "Comissão por Ação");
    XLSX.writeFile(livro, `template_comissao_acao_emitente_${emitenteConfiguracaoNumero}.xlsx`);
  };

  const importarArquivo = async (arquivo: File) => {
    if (!emitenteConfiguracaoNumero) return toast.error("Selecione o emitente antes de importar a planilha.");
    try {
      const conteudo = await arquivo.arrayBuffer();
      const livro = XLSX.read(conteudo);
      const planilha = livro.Sheets[livro.SheetNames[0]];
      const linhas: any[] = XLSX.utils.sheet_to_json(planilha, { defval: null });
      const regras: RegraImportada[] = [];
      const codigos = new Set<string>();
      for (const linha of linhas) {
        const productCod = String(linha[COLUNAS_TEMPLATE.codigo] || "").trim().toUpperCase();
        const valores = {
          vendaMinima: numeroOpcional(linha[COLUNAS_TEMPLATE.vendaMinima]),
          comissaoVendaMinima: numeroOpcional(linha[COLUNAS_TEMPLATE.comissaoVendaMinima]),
          venda2: numeroOpcional(linha[COLUNAS_TEMPLATE.venda2]),
          comissaoVenda2: numeroOpcional(linha[COLUNAS_TEMPLATE.comissaoVenda2]),
          venda3: numeroOpcional(linha[COLUNAS_TEMPLATE.venda3]),
          comissaoVenda3: numeroOpcional(linha[COLUNAS_TEMPLATE.comissaoVenda3]),
        };
        if (!productCod && Object.values(valores).every((valor) => valor === null)) continue;
        if (!productCod) throw new Error("Há uma linha com faixa preenchida e sem código de produto.");
        if (!produtosPorCodigo.has(productCod)) throw new Error(`Código não pertence ao emitente selecionado: ${productCod}`);
        if (codigos.has(productCod)) throw new Error(`Código repetido na planilha: ${productCod}`);
        codigos.add(productCod);
        const faixas = [
          [valores.vendaMinima, valores.comissaoVendaMinima],
          [valores.venda2, valores.comissaoVenda2],
          [valores.venda3, valores.comissaoVenda3],
        ];
        for (const [venda, comissao] of faixas) {
          if ((venda === null) !== (comissao === null)) throw new Error(`Venda e comissão devem ser preenchidas juntas para ${productCod}.`);
          if ((venda ?? 0) < 0 || (comissao ?? 0) < 0 || (comissao ?? 0) > 100) throw new Error(`Faixa inválida para ${productCod}.`);
        }
        const vendas = faixas.filter(([venda]) => venda !== null).map(([venda]) => Number(venda));
        if (vendas.length === 0) continue;
        if (vendas.some((venda, indice) => indice > 0 && venda <= vendas[indice - 1])) throw new Error(`As faixas devem ser crescentes para ${productCod}.`);
        regras.push({ productCod, ...valores });
      }
      if (!regras.length) throw new Error("A planilha não possui nenhuma regra de comissão preenchida.");
      setArquivoConfiguracao(arquivo);
      setRegrasImportadas(regras);
      toast.success(`${regras.length} regra(s) lida(s) da planilha.`);
    } catch (erro: any) {
      setArquivoConfiguracao(null);
      setRegrasImportadas([]);
      if (inputArquivo.current) inputArquivo.current.value = "";
      toast.error(erro.message || "Não foi possível ler a planilha.");
    }
  };

  const salvarConfiguracao = () => {
    if (!emitenteConfiguracaoNumero) return toast.error("Selecione o emitente.");
    if (!dataInicioConfiguracao || !dataFimConfiguracao) return toast.error("Informe a data inicial e final.");
    if (!vendedoresConfiguracao.length) return toast.error("Selecione ao menos um vendedor.");
    if (!arquivoConfiguracao || !regrasImportadas.length) return toast.error("Faça o upload de uma planilha com regras.");
    criarConfiguracao.mutate({
      emitenteId: emitenteConfiguracaoNumero,
      dataInicio: dataInicioConfiguracao,
      dataFim: dataFimConfiguracao,
      vendedorIds: vendedoresConfiguracao,
      nomePlanilha: arquivoConfiguracao.name,
      itens: regrasImportadas,
    });
  };

  const emitir = () => {
    if (!emitenteRelatorioNumero) return toast.error("Selecione o emitente.");
    if (!dataInicioRelatorio || !dataFimRelatorio) return toast.error("Informe a data inicial e final.");
    if (!vendedoresRelatorio.length) return toast.error("Selecione ao menos um vendedor.");
    emitirRelatorio.mutate({
      emitenteId: emitenteRelatorioNumero,
      dataInicio: dataInicioRelatorio,
      dataFim: dataFimRelatorio,
      vendedorIds: vendedoresRelatorio,
    });
  };

  const exportarConfiguracao = async (configuracao: any) => {
    try {
      const itens = await utils.comissaoAcaoVenda.exportarConfiguracao.fetch({ configuracaoId: Number(configuracao.id) });
      const linhas = (itens as any[]).map((item) => ({
        [COLUNAS_TEMPLATE.codigo]: item.productCod,
        [COLUNAS_TEMPLATE.descricao]: item.productDescription || "",
        [COLUNAS_TEMPLATE.preco]: Number(item.precoVendaCadastrado || 0),
        [COLUNAS_TEMPLATE.vendaMinima]: item.vendaMinima ?? "",
        [COLUNAS_TEMPLATE.comissaoVendaMinima]: item.comissaoVendaMinima ?? "",
        [COLUNAS_TEMPLATE.venda2]: item.venda2 ?? "",
        [COLUNAS_TEMPLATE.comissaoVenda2]: item.comissaoVenda2 ?? "",
        [COLUNAS_TEMPLATE.venda3]: item.venda3 ?? "",
        [COLUNAS_TEMPLATE.comissaoVenda3]: item.comissaoVenda3 ?? "",
      }));
      const livro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(livro, XLSX.utils.json_to_sheet(linhas), "Regras");
      XLSX.writeFile(livro, `configuracao_comissao_acao_${configuracao.id}.xlsx`);
    } catch (erro: any) {
      toast.error(erro.message || "Não foi possível exportar a configuração.");
    }
  };

  const exportarRelatorio = async (relatorio: any) => {
    try {
      const itens = await utils.comissaoAcaoVenda.exportarRelatorio.fetch({ relatorioId: Number(relatorio.id) });
      const linhas = (itens as any[]).map((item) => ({
        "PEDIDO": `#${item.pedidoNumero}`,
        "DATA CRIAÇÃO PEDIDO": formatarDataHora(item.pedidoCriadoEm),
        "VENDEDOR": item.vendedorNome,
        "CÓDIGO": item.productCod,
        "DESCRIÇÃO": item.productDescription || "",
        "PREÇO UNITÁRIO PRATICADO": Number(item.precoUnitarioPraticado || 0),
        "QUANTIDADE": Number(item.quantidade || 0),
        "VALOR BASE SEM IPI/ST": Number(item.valorBaseSemIpiSt || 0),
        "CONFIGURAÇÃO ID": item.configuracaoId ?? "",
        "PLANILHA DA CONFIGURAÇÃO": item.configuracaoNomePlanilha || "",
        "REGRA DE COMISSÃO": item.faixaAplicada,
        "COMISSÃO %": Number(item.percentualComissao || 0),
        "VALOR COMISSÃO": Number(item.valorComissao || 0),
      }));
      const livro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(livro, XLSX.utils.json_to_sheet(linhas), "Relatório");
      XLSX.writeFile(livro, `relatorio_comissao_acao_${relatorio.id}.xlsx`);
    } catch (erro: any) {
      toast.error(erro.message || "Não foi possível exportar o relatório.");
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Comissão por Ação de Venda</h1>
        <p className="mt-1 text-sm text-muted-foreground">Configure faixas percentuais por produto e emita relatórios de comissão por vendedor.</p>
      </div>

      <Tabs value={aba} onValueChange={setAba}>
        <TabsList>
          <TabsTrigger value="configurar">Configurar</TabsTrigger>
          <TabsTrigger value="relatorio">Relatório</TabsTrigger>
        </TabsList>

        <TabsContent value="configurar" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Nova configuração</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Emitente</label>
                  <Select value={emitenteConfiguracaoId} onValueChange={setEmitenteConfiguracaoId}>
                    <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
                    <SelectContent>{(emitentes as any[]).map((emitente) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.razaoSocial}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Data início</label>
                  <Input type="date" value={dataInicioConfiguracao} onChange={(event) => setDataInicioConfiguracao(event.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Data fim</label>
                  <Input type="date" value={dataFimConfiguracao} onChange={(event) => setDataFimConfiguracao(event.target.value)} />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Vendedores vinculados</label>
                <SeletorVendedores vendedores={vendedoresConfiguracaoData as any[]} selecionados={vendedoresConfiguracao} onChange={setVendedoresConfiguracao} disabled={!emitenteConfiguracaoNumero} />
              </div>

              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="outline" onClick={baixarTemplate} disabled={!emitenteConfiguracaoNumero || (produtosConfiguracao as any[]).length === 0}>
                  <Download className="mr-2 h-4 w-4" />Baixar template
                </Button>
                <input ref={inputArquivo} type="file" accept=".xlsx,.xls" className="hidden" onChange={(event) => { const arquivo = event.target.files?.[0]; if (arquivo) importarArquivo(arquivo); }} />
                <Button type="button" variant="outline" onClick={() => inputArquivo.current?.click()} disabled={!emitenteConfiguracaoNumero}>
                  <FileUp className="mr-2 h-4 w-4" />Upload planilha
                </Button>
                {arquivoConfiguracao && <span className="self-center text-sm text-muted-foreground">{arquivoConfiguracao.name} — {regrasImportadas.length} regra(s)</span>}
              </div>

              <Button type="button" onClick={salvarConfiguracao} disabled={criarConfiguracao.isPending}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />{criarConfiguracao.isPending ? "Salvando..." : "Salvar configuração"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Histórico de configurações</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left"><th className="p-2">Período</th><th className="p-2">Emitente</th><th className="p-2">Vendedores</th><th className="p-2">Planilha</th><th className="p-2">Regras</th><th className="p-2">Criado por</th><th className="p-2">Criado em</th><th className="p-2"></th></tr></thead>
                <tbody>{(configuracoes as any[]).map((configuracao) => <tr key={configuracao.id} className="border-b"><td className="p-2">{formatarData(configuracao.dataInicio)} a {formatarData(configuracao.dataFim)}</td><td className="p-2">{configuracao.emitenteNome}</td><td className="p-2">{configuracao.vendedores || "-"}</td><td className="p-2">{configuracao.nomePlanilha}</td><td className="p-2">{configuracao.totalRegras}</td><td className="p-2">{configuracao.criadoPorNome || "-"}</td><td className="p-2">{formatarDataHora(configuracao.criadoEm)}</td><td className="p-2"><Button size="sm" variant="outline" onClick={() => exportarConfiguracao(configuracao)}><Download className="mr-1 h-4 w-4" />Exportar</Button></td></tr>)}</tbody>
              </table>
              {(configuracoes as any[]).length === 0 && <p className="py-5 text-sm text-muted-foreground">Nenhuma configuração salva.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="relatorio" className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Emitir relatório de comissão</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Emitente</label>
                  <Select value={emitenteRelatorioId} onValueChange={setEmitenteRelatorioId}>
                    <SelectTrigger><SelectValue placeholder="Selecione o emitente" /></SelectTrigger>
                    <SelectContent>{(emitentes as any[]).map((emitente) => <SelectItem key={emitente.id} value={String(emitente.id)}>{emitente.razaoSocial}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Data início</label>
                  <Input type="date" value={dataInicioRelatorio} onChange={(event) => setDataInicioRelatorio(event.target.value)} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Data fim</label>
                  <Input type="date" value={dataFimRelatorio} onChange={(event) => setDataFimRelatorio(event.target.value)} />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Vendedores vinculados</label>
                <SeletorVendedores vendedores={vendedoresRelatorioData as any[]} selecionados={vendedoresRelatorio} onChange={setVendedoresRelatorio} disabled={!emitenteRelatorioNumero} />
              </div>

              <Button type="button" onClick={emitir} disabled={emitirRelatorio.isPending}>
                <Percent className="mr-2 h-4 w-4" />{emitirRelatorio.isPending ? "Emitindo..." : "Emitir relatório"}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Relatórios emitidos</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left"><th className="p-2">Período</th><th className="p-2">Emitente</th><th className="p-2">Vendedores</th><th className="p-2">Itens</th><th className="p-2">Total comissão</th><th className="p-2">Emitido em</th><th className="p-2"></th></tr></thead>
                <tbody>{(relatorios as any[]).map((relatorio) => <tr key={relatorio.id} className="border-b"><td className="p-2">{formatarData(relatorio.dataInicio)} a {formatarData(relatorio.dataFim)}</td><td className="p-2">{relatorio.emitenteNome}</td><td className="p-2">{nomesVendedores(relatorio.vendedoresJson)}</td><td className="p-2">{relatorio.totalItens}</td><td className="p-2">{formatarMoeda(relatorio.totalComissao)}</td><td className="p-2">{formatarDataHora(relatorio.emitidoEm)}</td><td className="p-2"><Button size="sm" variant="outline" onClick={() => exportarRelatorio(relatorio)}><Download className="mr-1 h-4 w-4" />Exportar</Button></td></tr>)}</tbody>
              </table>
              {(relatorios as any[]).length === 0 && <p className="py-5 text-sm text-muted-foreground">Nenhum relatório emitido.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
